import React from 'react';
import Button from 'aldoo-ra/Button';
import DropdownSelect from 'aldoo-ra/DropdownSelect';
import { Icons } from '../../components/Icon.jsx';
import { BREAKPOINTS } from '../../constants.js';
import { DevicePhoneMobileIcon, ComputerDesktopIcon, DeviceTabletIcon } from '@heroicons/react/24/outline';
import ThemeSwitcher from './ThemeSwitcher.jsx';

const EditorControls = ({
  isLiveMode,
  onSave,
  onToggleLiveMode,
  onShowSettings,
  isSaving,
  viewportWidth,
  setViewportWidth,
  activeBreakpoint,
  setActiveBreakpoint
}) => {

  const handleBreakpointChange = (bp, width) => {
    setActiveBreakpoint(bp);
    setViewportWidth(width === 0 ? 'auto' : width);
  };

  const breakpointOptions = [
      { value: 'xs', label: 'Small Mobile (XS)', width: BREAKPOINTS.xs, icon: DevicePhoneMobileIcon },
      { value: 'sm', label: 'Mobile (SM)', width: BREAKPOINTS.sm, icon: DevicePhoneMobileIcon },
      { value: 'md', label: 'Tablet (MD)', width: BREAKPOINTS.md, icon: DeviceTabletIcon },
      { value: 'lg', label: 'Laptop (LG)', width: BREAKPOINTS.lg, icon: ComputerDesktopIcon },
      { value: 'xl', label: 'Desktop (XL)', width: BREAKPOINTS.xl, icon: ComputerDesktopIcon, isDefault: true },
      { value: '2xl', label: 'Wide (2XL)', width: BREAKPOINTS['2xl'], icon: ComputerDesktopIcon },
  ];

  const selectedOption = breakpointOptions.find(opt => opt.value === activeBreakpoint) || breakpointOptions[0];

  return (
    <div className="flex items-center gap-4">
      
      {/* Viewport Controls */}
      <div className="mr-2">
         <DropdownSelect
            options={breakpointOptions}
            value={[selectedOption]}
            onChange={(val) => {
                const selection = Array.isArray(val) ? val[0] : val;
                if (selection) handleBreakpointChange(selection.value, selection.width);
            }}
            showCheckbox={false}
            enableResetButton={false}
            variant="admin"
            className="!p-0 !m-0"
            dropdownWidth="14rem"
            customTrigger={
                <div className="flex items-center gap-2 px-2 py-1 bg-admin_neutral_1/10 hover:bg-admin_neutral_1/20 border border-admin_bordered rounded cursor-pointer transition-colors min-w-[11rem]">
                    {React.createElement(selectedOption.icon, { className: "w-4 h-4 text-admin_text_secondary" })}
                    <div className="flex flex-col items-start leading-none">
                        <span className="text-xs font-bold text-admin_text">{selectedOption.label}</span>
                        <span className="text-[9px] text-admin_text_secondary">{selectedOption.width === 0 ? '100%' : `${selectedOption.width}px`}</span>
                    </div>
                    <Icons.ChevronDown className="w-3 h-3 ml-auto text-admin_text_secondary opacity-50" />
                </div>
            }
            onItemRender={(opt) => (
                <div className="flex items-center justify-between w-full py-1">
                    <div className="flex items-center gap-2">
                        {React.createElement(opt.icon, { className: "w-4 h-4 text-admin_text_secondary" })}
                        <span className="text-xs font-medium text-admin_text">{opt.label}</span>
                    </div>
                    <span className="text-[10px] font-mono text-admin_text_secondary bg-admin_neutral_1/10 px-1 rounded">
                        {opt.width === 0 ? 'Auto' : `${opt.width}px`}
                    </span>
                </div>
            )}
         />
      </div>

      <div className="w-px h-4 bg-admin_bordered" />

      <ThemeSwitcher />

      <button
        onClick={onShowSettings}
        className="flex items-center gap-1.5 px-3 py-1 rounded text-xs font-bold transition-all bg-admin_neutral_1/10 hover:bg-admin_neutral_1/20 text-admin_text"
      >
        <Icons.Settings className="w-3 h-3" /> SETTINGS
      </button>
      <button
        onClick={onSave}
        disabled={isSaving}
        className="flex items-center gap-1.5 px-3 py-1 rounded text-xs font-bold transition-all bg-blue-600 hover:bg-blue-500 text-white disabled:opacity-50"
      >
        {isSaving ? 'SAVING...' : 'SAVE'}
      </button>
      <button
        onClick={onToggleLiveMode}
        className={`flex items-center gap-1.5 px-3 py-1 rounded text-xs font-bold transition-all ${
          isLiveMode
            ? 'bg-red-500 hover:bg-red-600 text-white shadow-[0_0_10px_rgba(239,68,68,0.5)]'
            : 'bg-green-600 hover:bg-green-500 text-white'
        }`}
      >
        {isLiveMode ? (
          <>
            <Icons.Stop className="w-3 h-3" fill="currentColor" />
            STOP
          </>
        ) : (
          <>
            <Icons.Play className="w-3 h-3" fill="currentColor" />
            PLAY
          </>
        )}
      </button>
    </div>
  );
};

export default EditorControls;